import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSignal } from 'beatlejs/integrations/react';
import { $Auth } from '@/services/$AuthService';
import { AccessDenied } from '@/components/shared/AccessDenied';
import { getRouteAccessRule, hasAdminAccess } from '@/utils/adminAccess';

interface RequireAuthProps {
  children: React.ReactNode;
}

export const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  const [auth] = useSignal([$Auth]);
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (!auth.isLoggedIn) {
        await auth.checkAuthStatus();
      }
      setIsChecking(false);
    };

    checkAuth();
  }, []); // Only run once on mount

  // If we're checking auth status or loading, show loading
  if (isChecking || auth.isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">در حال بررسی احراز هویت...</p>
        </div>
      </div>
    );
  }

  // If not logged in after checking, redirect to login
  if (!auth.isLoggedIn) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const routeAccessRule = getRouteAccessRule(location.pathname);
  if (routeAccessRule && !hasAdminAccess(auth.user, routeAccessRule)) {
    return <AccessDenied />;
  }

  // User is logged in, show protected content
  return <>{children}</>;
};
